import { DefaultEsQueryConfig, MuiDirectionToEsDirection } from 'lib/constants';
import {
    ElasticsearchDeviceBodyConfig,
    ElasticsearchDirection,
} from 'types/ElasticsearchQueries';
import { UserDeviceSession } from 'types/User/firebaseTypes';
import { getIsDeviceOfflineFiltersForES } from '../../helpers/device-offline';

/** Return the ES equivalent of the inputted MUI sort order. Default to DESC if no valid value is provided. */
export const getEsDirectionFromMui = (
    muiDirection: string
): ElasticsearchDirection => {
    if (MuiDirectionToEsDirection[muiDirection])
        return MuiDirectionToEsDirection[muiDirection];
    else return DefaultEsQueryConfig.direction ?? 'DESC';
};

/**
 * Some data table/data grid `field` names do not match up with a field name ES is able to work with/expects.
 * ES is only able to search/sort/filter on fields it indexes. Here's our current device ES index list https://bitbucket.org/tankutility/tu-es/src/master/conf/es-devices.json
 */
export const getTankTableSortField = (field: string): string => {
    switch (field) {
        case 'health':
            return 'lastReading.time';
        case 'lastReading':
            return 'lastReading.battery';
        case 'back_office_sync_time_key':
            return 'bo_sync_time';
        case 'l30':
            return 'lastReading.tank';
        case 'product_name':
            return 'product_name.keyword';
        case 'product_id':
            return 'product_id.keyword';
        case 'mfg_tla_rev':
            return 'mfg_tla_rev.keyword';
        case 'mfg_tla_dev':
            return 'mfg_tla_dev.keyword';
        case 'est_delivery_date':
            return 'duf_7';
        default:
            return field;
    }
};

/**
 * Helper function to construct an Elasticsearch json_query body for device search based on various filtering/search/sort/etc criteria.
 * @param {string} supplierId
 * @param {ElasticsearchDeviceBodyConfig} queryConfig
 * @returns Device search json_query param
 */
export const getEsDeviceSearchJsonQuery = (
    supplierId: string,
    queryConfig?: ElasticsearchDeviceBodyConfig
): any => {
    const hiddenStatuses = ['returned', 'retired', 'return-arrived'];
    const mustNot = queryConfig?.isAdminUser
        ? []
        : [{ terms: { status: hiddenStatuses } }];

    const jsonQuery: any = {
        query: {
            bool: {
                filter: {
                    bool: {
                        must: [
                            {
                                term: {
                                    virtual_supplier: supplierId,
                                },
                            },
                        ],
                        must_not: mustNot,
                    },
                },
            },
        },
    };

    // Filters based on user session data
    if (queryConfig?.sessionFilters) {
        // filter: consumption type
        if (shouldFilterByConsumptionType(queryConfig.sessionFilters)) {
            const consumptionQuery = buildConsumptionTypeQuery(
                queryConfig.sessionFilters
            );
            jsonQuery.query.bool.filter.bool.must.push(consumptionQuery);
        }

        // filter: status
        if (!queryConfig.sessionFilters.misc?.all_status) {
            // Note: inactive pushes to the `must_not` array.
            if (
                queryConfig.sessionFilters.misc?.inactive &&
                !queryConfig.sessionFilters.misc.active
            ) {
                if (queryConfig.isAdminUser) {
                    jsonQuery.query.bool.filter.bool.must_not.push({
                        term: { status: 'deployed' },
                    });
                } else {
                    // Even if we are showing inactive devices, do not show returned, retired, or return-arrived
                    // devices to non-admin users SW-4472.
                    jsonQuery.query.bool.filter.bool.must_not.push({
                        terms: {
                            status: [...hiddenStatuses, 'deployed'],
                        },
                    });
                }
            } else if (
                queryConfig.sessionFilters.misc?.active &&
                !queryConfig.sessionFilters.misc?.inactive
            ) {
                const statusQuery = buildStatusMustQuery(
                    queryConfig.sessionFilters
                );
                jsonQuery.query.bool.filter.bool.must.push(statusQuery);
            }
        }

        // filter: monitor type (e.g., virtual vs physical)
        if (
            queryConfig.sessionFilters.misc &&
            shouldFilterByMonitorType(queryConfig.sessionFilters)
        ) {
            if (queryConfig.sessionFilters.misc?.virtual) {
                jsonQuery.query.bool.filter.bool.must.push({
                    term: { connection_type: 'virtual' },
                });
            }

            if (queryConfig.sessionFilters.misc?.physical) {
                jsonQuery.query.bool.filter.bool.must_not.push({
                    term: { connection_type: 'virtual' },
                });
            }
        }

        // filter: group
        if (queryConfig.groups) {
            if (shouldFilterByGroup(queryConfig.groups)) {
                const groupQuery = buildGroupQuery(queryConfig.groups);
                jsonQuery.query.bool.filter.bool.must.push(groupQuery);
            }
        }

        // filter: delivery type
        if (queryConfig.sessionFilters.delivery_type) {
            if (shouldFilterByDeliveryType(queryConfig.sessionFilters)) {
                const deliveryTypeQuery = buildDeliveryTypeQuery(
                    queryConfig.sessionFilters
                );
                jsonQuery.query.bool.filter.bool.must.push(deliveryTypeQuery);
            }
        }
    }

    // Filter by a single device status (not based on user session)
    if (queryConfig?.statusFilter) {
        const statusQuery = {
            term: { status: queryConfig.statusFilter },
        };
        jsonQuery.query.bool.filter.bool.must.push(statusQuery);
    }

    // Filter to include only offline devices. Not based on user session.
    if (queryConfig?.offlineOnly) {
        // Add filter array from device-offline.ts to the must array.
        jsonQuery.query.bool.filter.bool.must.push(
            ...getIsDeviceOfflineFiltersForES()
        );
    }

    // Filter to include only deployed devices that have battery_crit < 0 or battery_warn < 0.
    if (queryConfig?.lowBatteryOnly) {
        jsonQuery.query.bool.filter.bool.must.push(buildLowBatteryQuery());
    }

    // Filter to exclude virtual devices (not based on user session).
    if (queryConfig?.excludeVirtualDevices) {
        jsonQuery.query.bool.filter.bool.must_not.push({
            term: { connection_type: 'virtual' },
        });
    }

    // Keyword search
    if (queryConfig?.search) {
        const searchTerms = queryConfig.search
            .replaceAll(',', ' ') // remove commas if user tries to separate terms with them
            .split(' ') // get array of words/terms
            .filter((term) => term); // get rid of trailing or leading spaces
        jsonQuery.query.bool.must = jsonQuery.query.bool.must ?? [];
        const searchQueries = searchTerms.map((term, idx) => ({
            query_string: {
                ...(queryConfig.searchFields
                    ? { fields: queryConfig.searchFields }
                    : { default_field: '*' }),
                query:
                    (term ?? DefaultEsQueryConfig.search) +
                    (idx === searchTerms.length - 1 ? '*' : ''), // add wildcard to last item to "predict" search
            },
        }));
        jsonQuery.query.bool.must.push(...searchQueries);
    }

    // Searching from short device ids. Used mostly in shipments page to get all devices in a shipment.
    if (queryConfig?.shortIdQueryString) {
        // Reset the bool.filter entry of the query.
        jsonQuery.query.bool.filter = {};
        // Initialize the match entry.
        jsonQuery.query.bool.filter.match = {};

        jsonQuery.query.bool.filter.match.shortDeviceId =
            queryConfig.shortIdQueryString;
    }

    return jsonQuery;
};

/** Determine if monitor type filters should be included in the device search query. If all or no monitor type filters are on, we do not want to include monitor type filters in the ES query. */
const shouldFilterByMonitorType = (
    filters: Partial<UserDeviceSession['filters']>
): boolean => {
    if (!filters.misc) return false;

    const bothEnabled = filters.misc.virtual && filters.misc.physical;
    const neitherEnabled = !filters.misc.virtual && !filters.misc.physical;

    if (bothEnabled || neitherEnabled) return false;
    else return true;
};

/** Determine if consumption type filters should be included in the device search query. If all or no consumption type filters are on, we do not want to include consumption type filters in the ES query. */
const shouldFilterByConsumptionType = (
    filters: Partial<UserDeviceSession['filters']>
): boolean => {
    if (!filters.consumptionType) return false;

    const numberOfEnabledConsumptionTypeFilters = Object.values(
        filters.consumptionType
    ).filter((val) => val === true).length;
    const numberOfConsumptionTypes = Object.values(
        filters.consumptionType
    ).length;

    // If no consumption type filters are active OR if ALL consumption type filters are active, we
    // are effectively just not filtering by consumption type.
    const filterByConsumptionType =
        numberOfEnabledConsumptionTypeFilters === 0 ||
        numberOfEnabledConsumptionTypeFilters === numberOfConsumptionTypes
            ? false
            : true;

    return filterByConsumptionType;
};

/** Determine if delivery type filters should be included in the device search query. If all or no delivery type filters are on, we do not want to include delivery type filters in the ES query. */
const shouldFilterByDeliveryType = (
    filters: Partial<UserDeviceSession['filters']>
): boolean => {
    if (!filters.delivery_type) return false;

    const numberOfEnabledDeliveryTypeFilters = Object.values(
        filters.delivery_type
    ).filter((val) => val === true).length;

    // If no delivery type filters are active OR if ALL delivery type filters are active, we
    // are effectively just not filtering by delivery type.
    const filterByDeliveryType =
        numberOfEnabledDeliveryTypeFilters === 0 ? false : true;
    return filterByDeliveryType;
};

/** Determine if group filters should be included in the device search query. If all or no group filters are on, we do not want to include group filters in the ES query. */
const shouldFilterByGroup = (
    groupFilters: Record<string, boolean>
): boolean => {
    if (!groupFilters) return false;

    const numberOfEnabledGroupFilters = Object.values(groupFilters).filter(
        (val) => val === true
    ).length;
    const numberOfGroups = Object.values(groupFilters).length;

    // If no group filters are active OR if ALL group filters are active, we
    // are effectively just not filtering by group .
    const filterByGroup =
        numberOfEnabledGroupFilters === 0 ||
        numberOfEnabledGroupFilters === numberOfGroups
            ? false
            : true;
    return filterByGroup;
};

/** Helper function to construct the consumption type filters part of the the device search query. */
const buildConsumptionTypeQuery = (
    filters: Partial<UserDeviceSession['filters']>
): any => {
    const query: any = {
        nested: {
            path: 'consumptionType',
            score_mode: 'avg',
            query: {
                bool: {
                    should: [{ match: { 'consumptionType.none': true } }],
                },
            },
        },
    };

    for (const c in filters.consumptionType) {
        if (filters.consumptionType[c]) {
            query.nested.query.bool.should.push({
                match: { ['consumptionType.' + c]: true },
            });
        }
    }

    return query;
};

/** Helper function to construct the monitor status filters `must` part of the device search query. */
const buildStatusMustQuery = (
    filters: Partial<UserDeviceSession['filters']>
): any => {
    if (filters?.misc?.active) {
        return {
            term: { status: 'deployed' },
        };
    } else {
        // Note: the `terms` plural here.
        return {
            terms: { status: ['deployed', 'shipped', 'sleep'] },
        };
    }
};

/** Helper function to construct the delivery types filters part of the device search query. */
const buildDeliveryTypeQuery = (
    filters: Partial<UserDeviceSession['filters']>
): any => {
    const deliveryTypes = ['none'];

    for (const deliveryType in filters.delivery_type) {
        if (filters.delivery_type[deliveryType]) {
            deliveryTypes.push(deliveryType);
        }
    }

    const query: any = {
        terms: {
            delivery_type: deliveryTypes,
        },
    };

    return query;
};

/** Helper function to construct the groups filters part of the device search query. */
const buildGroupQuery = (groupFilters: Record<string, boolean>): any => {
    const groups: string[] = [];
    Object.entries(groupFilters).forEach((entry: [string, boolean]) => {
        const groupName: string = entry[0];
        const groupActive: boolean = entry[1];
        if (groupActive) groups.push(groupName);
    });

    const query: any = {
        terms: {
            group: groups,
        },
    };
    return query;
};

/** Helper function to construct a query to get all devices with battery_crit < 0 or battery_warn < 0. */
const buildLowBatteryQuery = (): any => {
    return {
        bool: {
            // should is like OR. must is like AND.
            should: [
                {
                    range: {
                        'lastReading.battery_crit': {
                            lt: 0,
                        },
                    },
                },
                {
                    range: {
                        'lastReading.battery_warn': {
                            lt: 0,
                        },
                    },
                },
            ],
            minimum_should_match: 1, // Make sure at least one of the should conditions is met.
        },
    };
};

export const getMapViewJsonQuery = (
    supplierId: string,
    queryConfig: ElasticsearchDeviceBodyConfig
) => {
    const filteredJsonQuery = getEsDeviceSearchJsonQuery(
        supplierId,
        queryConfig
    );

    const modifiedMapQuery = {
        ...filteredJsonQuery,
        query: {
            ...filteredJsonQuery.query,
            bool: {
                ...filteredJsonQuery.query.bool,
                must: [
                    { exists: { field: 'lat' } },
                    { exists: { field: 'lng' } },
                ],
            },
        },
    };

    return modifiedMapQuery;
};
